import { isSignInWithEmailLink, signInWithEmailLink, fetchSignInMethodsForEmail, signInAnonymously, GoogleAuthProvider, signInWithPopup, signInWithCustomToken, signInWithEmailAndPassword, EmailAuthProvider, linkWithCredential, createUserWithEmailAndPassword, sendPasswordResetEmail, checkActionCode, confirmPasswordReset, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { Authentication } from "../../interfaces/authentication.js";
const USER_NOT_AUTHENTICATED = "User is not authenticated.";
class AuthAdapter extends Authentication {
  constructor(instance) {
    super();
    this.instance = instance;
  }
  isSignInWithEmailLink(link) {
    return isSignInWithEmailLink(this.instance, link);
  }
  async signInWithEmailLink(email, link) {
    return signInWithEmailLink(this.instance, email, link).then(
      (userCredential) => this.getUser(userCredential.user)
    );
  }
  getCurrentUser() {
    return this.instance.currentUser;
  }
  getIdToken() {
    const currentUser = this.instance.currentUser;
    if (currentUser) {
      return currentUser.getIdToken();
    } else {
      return Promise.reject(USER_NOT_AUTHENTICATED);
    }
  }
  async getUser(user) {
    if (user) {
      const token = await user.getIdToken();
      return {
        providerData: user.providerData,
        emailVerified: user.emailVerified,
        displayName: user.displayName || "",
        email: user.email || "",
        photoURL: user.photoURL || "",
        uid: user.uid || "",
        token
      };
    } else {
      return {
        providerData: [],
        emailVerified: false,
        displayName: "",
        email: "",
        photoURL: "",
        uid: "",
        token: ""
      };
    }
  }
  emailAlreadyRegistered(email) {
    return fetchSignInMethodsForEmail(this.instance, email);
  }
  signInAnonymously() {
    return signInAnonymously(this.instance);
  }
  async signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    provider.addScope("profile");
    provider.addScope("email");
    const credential = await signInWithPopup(this.instance, provider);
    return this.getUser(credential.user);
  }
  async signInWithCustomToken(customToken) {
    const credential = await signInWithCustomToken(this.instance, customToken);
    return this.getUser(credential.user);
  }
  async signInWithEmailAndPassword(email, password) {
    if (this.instance.currentUser) {
      const credential = await signInWithEmailAndPassword(
        this.instance,
        email,
        password
      );
      return this.getUser(credential.user);
    } else {
      const credential = await signInWithEmailAndPassword(
        this.instance,
        email,
        password
      );
      return this.getUser(credential.user);
    }
  }
  async createUserWithEmailAndPassword(email, password) {
    if (this.instance.currentUser) {
      const credential = EmailAuthProvider.credential(email, password);
      const credentialed = await linkWithCredential(
        this.instance.currentUser,
        credential
      );
      return this.getUser(credentialed.user);
    } else {
      const credential = await createUserWithEmailAndPassword(
        this.instance,
        email,
        password
      );
      return this.getUser(credential.user);
    }
  }
  signOut() {
    return this.instance.signOut();
  }
  sendPasswordResetEmail(email) {
    return sendPasswordResetEmail(this.instance, email);
  }
  checkActionCode(code) {
    return checkActionCode(this.instance, code);
  }
  confirmPasswordReset(code, password) {
    return confirmPasswordReset(this.instance, code, password);
  }
  getCurrentClient() {
    return this.instance.currentUser;
  }
  onIdTokenChanged(nextOrObserver, error, completed) {
    return this.instance.onIdTokenChanged(
      async (u) => {
        const auth = await this.getUser(u);
        nextOrObserver(u, auth);
      },
      error,
      completed
    );
  }
  reauthenticateWithCredential(currentPassword) {
    const currentUser = this.getCurrentClient();
    try {
      if (currentUser) {
        const credential = EmailAuthProvider.credential(
          currentUser.email,
          currentPassword
        );
        return reauthenticateWithCredential(currentUser, credential);
      } else {
        return Promise.reject(USER_NOT_AUTHENTICATED);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }
  async recoveryPassword(code, newPassword) {
    try {
      const response = await this.checkActionCode(code);
      await confirmPasswordReset(this.instance, code, newPassword);
      await this.signInWithEmailAndPassword(
        response.data.email,
        newPassword
      );
      return { email: response.data.email };
    } catch (e) {
      return Promise.reject(e);
    }
  }
  async updatePassword(currentPassword, newPassword) {
    try {
      const currentUser = this.getCurrentClient();
      if (currentUser) {
        await this.reauthenticateWithCredential(currentPassword);
        await updatePassword(currentUser, newPassword);
        return { email: currentUser.email };
      } else {
        return Promise.reject(USER_NOT_AUTHENTICATED);
      }
    } catch (e) {
      return Promise.reject(e);
    }
  }
}
export {
  AuthAdapter
};
//# sourceMappingURL=auth.adapter.js.map
